import { useEffect, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { featuredArticles, faqItems } from "@/mocks/articles";

const SITE_URL = "https://kakobuyspreadsheet.watch";

const articleHero = "https://readdy.ai/api/search-image?query=Clean%20minimalist%20editorial%20desk%20setup%20with%20a%20notebook%20laptop%20and%20product%20catalog%20on%20a%20warm%20wooden%20surface%20with%20soft%20window%20lighting%20modern%20workspace%20photography&width=900&height=500&seq=art-hero-generic-v2&orientation=landscape";

const articleContent: Record<string, { sections: { title: string; paragraphs: string[]; list?: { ordered: boolean; items: string[] }; tip?: string }[]; readingTime: string }> = {
  "/co-to-jest-kakobuy-spreadsheet": {
    readingTime: "8 minut",
    sections: [
      {
        title: "Czym Właściwie Jest Kakobuy Spreadsheet?",
        paragraphs: [
          "Kakobuy Spreadsheet to zaawansowana, codziennie aktualizowana baza produktowa, która agreguje setki, a nawet tysiące zweryfikowanych ofert z chińskich platform zakupowych w jednym, przejrzystym arkuszu.",
          "Dla polskich kupujących, którzy chcą robić zakupy w Chinach bez znajomości chińskiego języka i bez ryzyka trafienia na oszustów, Kakobuy Spreadsheet stanowi absolutnie niezbędne narzędzie. Zamiast spędzać godziny na przeszukiwaniu forów, otrzymujesz gotową, zweryfikowaną listę produktów.",
          "Każdy produkt w naszym spreadsheet przechodzi wieloetapową weryfikację — sprawdzamy jakość zdjęć, opinie innych kupujących, historię sprzedawcy oraz aktualność oferty.",
        ],
      },
      {
        title: "Dlaczego Warto Korzystać z Kakobuy Spreadsheet w 2026 Roku?",
        paragraphs: [
          "Rok 2026 przyniósł wiele zmian na rynku agentów zakupowych. Kakobuy Spreadsheet wypełnia tę lukę oferując stabilne źródło informacji.",
        ],
        list: {
          ordered: false,
          items: [
            "Codzienna aktualizacja — każdy produkt sprawdzany minimum raz na 24 godziny",
            "14 kategorii produktowych — od butów po akcesoria",
            "Zweryfikowani sprzedawcy z udokumentowaną historią",
            "Bezpieczne linki przez oficjalny sklep kaspreadsheet.com",
            "Całkowicie darmowy dostęp bez rejestracji",
          ],
        },
      },
      {
        title: "Jak Działa Kakobuy Spreadsheet?",
        paragraphs: [
          "Mechanizm działania jest prosty. Nasz zespół codziennie monitoruje chińskie platformy zakupowe, wyszukując najlepsze oferty.",
        ],
        list: {
          ordered: true,
          items: [
            "Identyfikacja produktu — znajdujemy popularne produkty z polecanych źródeł",
            "Weryfikacja sprzedawcy — sprawdzamy historię, opinie i jakość obsługi",
            "Kontrola jakości — analizujemy zdjęcia, ceny i stosunek jakości do ceny",
            "Dodanie do arkusza — produkt trafia do odpowiedniej kategorii z linkiem i oceną",
            "Ciągły monitoring — oferty są regularnie sprawdzane pod kątem aktualności",
          ],
        },
      },
    ],
  },
  "/jak-zamawiac-kakobuy": {
    readingTime: "6 minut",
    sections: [
      {
        title: "Jak Zamawiać przez Kakobuy — Instrukcja Krok po Kroku",
        paragraphs: [
          "Zamawianie przez Kakobuy Spreadsheet jest prostsze niż myślisz. Oto kompletny przewodnik, który przeprowadzi Cię przez cały proces — od wyboru produktu po otrzymanie przesyłki.",
        ],
        list: {
          ordered: true,
          items: [
            "Wybierz kategorię i znajdź interesujący produkt w naszym spreadsheet",
            "Kliknij w link, który przekieruje Cię do oficjalnego sklepu kaspreadsheet.com",
            "Sprawdź dostępność, rozmiar i cenę produktu",
            "Dodaj produkt do koszyka i przejdź do płatności",
            "Wybierz preferowaną metodę wysyłki i opłać zamówienie",
            "Śledź status przesyłki przez panel klienta na kaspreadsheet.com",
            "Odbierz paczkę i ciesz się zakupem",
          ],
        },
        tip: "Zawsze porównuj ceny między różnymi ofertami w spreadsheet — różnice mogą sięgać nawet 30%!",
      },
    ],
  },
  "/kakobuy-vs-pandabuy": {
    readingTime: "7 minut",
    sections: [
      {
        title: "Kakobuy vs Pandabuy — Szczegółowe Porównanie 2026",
        paragraphs: [
          "Porównanie dwóch gigantów rynku agentów zakupowych. Sprawdzamy ceny, szybkość wysyłki, jakość obsługi klienta i bezpieczeństwo transakcji.",
        ],
      },
    ],
  },
  "/bezpieczne-zakupy-chiny": {
    readingTime: "5 minut",
    sections: [
      {
        title: "Bezpieczne Zakupy w Chinach — Kompletny Poradnik",
        paragraphs: [
          "Zakupy na chińskich platformach mogą być bezpieczne, jeśli wiesz jak się do nich przygotować. Oto sprawdzone zasady, które uchronią Cię przed oszustwami.",
        ],
        list: {
          ordered: false,
          items: [
            "Korzystaj wyłącznie ze zweryfikowanych linków z Kakobuy Spreadsheet",
            "Nigdy nie wysyłaj pieniędzy bezpośrednio sprzedawcy — używaj oficjalnych platform",
            "Porównuj ceny między różnymi ofertami tego samego produktu",
            "Czytaj opinie innych kupujących przed dokonaniem zakupu",
            "Zachowaj potwierdzenia płatności i korespondencję ze sprzedawcą",
          ],
        },
        tip: "Wszystkie linki w naszym spreadsheet prowadzą przez kaspreadsheet.com, który zapewnia pełną ochronę kupującego.",
      },
    ],
  },
};

const defaultContent = {
  readingTime: "6 minut",
  sections: [
    {
      title: "Szczegółowy Przewodnik",
      paragraphs: [
        "Zapraszamy do lektury naszego szczegółowego przewodnika. Poniżej znajdziesz wszystkie niezbędne informacje, które pomogą Ci podjąć właściwą decyzję zakupową.",
        "Nasz zespół Kakobuy Spreadsheet regularnie aktualizuje treści, aby dostarczać Ci najbardziej aktualne i rzetelne informacje o produktach, sprzedawcach i trendach na rynku.",
      ],
      list: {
        ordered: false,
        items: [
          "Zawsze aktualne dane z codzienną weryfikacją",
          "Sprawdzone linki do zweryfikowanych sprzedawców",
          "Praktyczne porady zakupowe",
          "Analizy i porównania agentów zakupowych",
        ],
      },
    },
    {
      title: "Dlaczego Kakobuy Spreadsheet?",
      paragraphs: [
        "Kakobuy Spreadsheet to nie tylko baza produktów — to kompleksowe narzędzie dla każdego, kto poważnie podchodzi do zakupów na chińskich platformach.",
        "Dzięki naszemu arkuszowi zaoszczędzisz godziny na samodzielnym przeszukiwaniu forów i weryfikacji sprzedawców. Wszystko, czego potrzebujesz, znajduje się w jednym miejscu.",
      ],
      tip: "Pamiętaj — dostęp do pełnej bazy Kakobuy Spreadsheet jest całkowicie darmowy. Nie wymagamy rejestracji ani logowania.",
    },
  ],
};

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = useMemo(() => featuredArticles.find((a) => a.slug === slug), [slug]);
  const content = articleContent[slug] || defaultContent;

  const articleJSONLD = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.seoTitle,
    "description": article.seoDescription,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": { "@type": "Organization", "name": "Kakobuy Spreadsheet", "url": SITE_URL },
    "publisher": { "@type": "Organization", "name": "Kakobuy Spreadsheet", "url": SITE_URL },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}${article.slug}` },
    "inLanguage": "pl-PL",
  } : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-[72px] min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-6">
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-4">Artykuł nie został znaleziony</h1>
            <RouterLink to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              Powrót na stronę główną
            </RouterLink>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={article.seoTitle}
        description={article.seoDescription}
        keywords={article.seoKeywords}
        canonicalPath={article.slug}
        ogType="article"
        jsonLd={[articleJSONLD]}
      />
      <Navbar />
      <main>
        <article>
          {/* Hero */}
          <section className="relative pt-[72px] bg-background-100">
            <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-16 pb-12 md:pt-24 md:pb-16">
              <RouterLink
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors mb-8 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Powrót na stronę główną
              </RouterLink>

              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {article.category}
              </span>

              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed mb-6 max-w-2xl">{article.excerpt}</p>

              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {content.readingTime} czytania
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  Zespół Kakobuy Spreadsheet
                </span>
              </div>
            </div>
          </section>

          {/* Article image */}
          <section className="bg-background-100 pb-4">
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
              <div className="rounded-xl overflow-hidden">
                <img
                  src={articleHero || "https://readdy.ai/api/search-image?query=Clean%20minimalist%20editorial%20desk%20setup%20with%20a%20notebook%20laptop%20and%20product%20catalog%20on%20a%20warm%20wooden%20surface%20with%20soft%20window%20lighting%20modern%20workspace%20photography&width=900&height=500&seq=art-hero-generic-v2&orientation=landscape"}
                  alt={article.title}
                  className="w-full h-[300px] md:h-[400px] object-cover"
                />
              </div>
            </div>
          </section>

          {/* Article content */}
          <section className="bg-background-50">
            <div className="max-w-[900px] mx-auto px-6 md:px-10 py-12 md:py-16">
              <div className="prose-custom max-w-none">
                {content.sections.map((section, sIdx) => (
                  <div key={sIdx}>
                    <h2 className="font-heading font-bold text-2xl text-foreground-950 mt-10 mb-4">
                      {section.title}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4">
                        {p}
                      </p>
                    ))}
                    {section.list && section.list.ordered && (
                      <ol className="list-decimal pl-6 mb-6 space-y-2 text-foreground-700 leading-relaxed">
                        {section.list.items.map((item, lIdx) => (
                          <li key={lIdx}>{item}</li>
                        ))}
                      </ol>
                    )}
                    {section.list && !section.list.ordered && (
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-foreground-700 leading-relaxed">
                        {section.list.items.map((item, lIdx) => (
                          <li key={lIdx}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {section.tip && (
                      <div className="bg-background-100 rounded-xl p-6 md:p-8 my-8 border border-background-200/70">
                        <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">Wskazówka</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{section.tip}</p>
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex flex-wrap gap-4 mt-10 pt-8 border-t border-background-200/70">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Przejdź do Kaspreadsheet.com
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <RouterLink
                    to="/"
                    className="inline-flex items-center px-6 py-3 rounded-full border border-background-300 text-foreground-700 font-semibold text-sm hover:border-foreground-400 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Powrót na stronę główną
                  </RouterLink>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100">
            <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-20">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10">
                Najczęściej Zadawane Pytania
              </h2>
              <div className="space-y-6">
                {faqItems.map((item, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                    <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">
                      {item.question}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Related articles */}
          <section className="bg-background-50">
            <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-20">
              <h2 className="font-heading font-bold text-2xl text-foreground-950 mb-8">
                Powiązane Artykuły
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {featuredArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 3)
                  .map((a) => (
                    <RouterLink
                      key={a.slug}
                      to={a.slug}
                      className="block p-5 bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="text-xs text-accent-500 font-medium">{a.category}</span>
                      <h3 className="font-heading font-semibold text-sm text-foreground-950 mt-2 mb-1">
                        {a.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">{a.excerpt}</p>
                    </RouterLink>
                  ))}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <RouterLink
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Powrót na stronę główną — Kakobuy Spreadsheet
                </RouterLink>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}